import React, { useState, useEffect, useRef, useMemo } from 'react'
import AssetService from "../../services/AssetService";
import FacilityService from "../../services/FacilityService";
import UserService from "../../services/UserService";
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdDatepicker } from "../../utils/Antd";
import util from "../../utils/util";
import moment from "moment";
import downloa from "../assets/icons/download-solid.svg"
import TicketInventoryService from '../../services/TicketInventoryService'
import MaintenanceReportUtil from '../../utils/MaintenanceReportUtil';
import { saveAs } from 'file-saver';
import excel from "../assets/icons/excel.png"; 
import acrobat from "../assets/icons/acrobat.png";
import search from "../assets/icons/magnifying-glass-solid.svg"

import {
    Input,
    Button,
    message,
    Card,
    Statistic,
    Progress,
    Row,
    Col,
    Table,
    Tag,
    Select,
    DatePicker,
    Alert
} from 'antd';

import {
    BarChartOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    ToolOutlined,
    DollarOutlined,
    CalendarOutlined,
    WarningOutlined,
    RiseOutlined,
    FallOutlined,
    BugOutlined,
    SafetyOutlined
} from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Maintenance = () => {
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [infrastuctureSubCats, setInfrastuctureSubCats] = useState([]);
    const [assets, setAssets] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [detailsResult, setDetailsResult] = useState({ data: [] });
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(true);
    const [analyticsDateRange, setAnalyticsDateRange] = useState([null, null]);
    const [selectedAnalyticsType, setSelectedAnalyticsType] = useState('all');
    
    const [result, setResult] = useState({ data: [], page: {} });
    const sdataRef = useRef({ p: 1, ps: 25 });
    const isNational = util.isNational();

    const ticketStatusNames = [
        { id: 1, name: 'Open' },
        { id: 2, name: 'On Hold - Awaiting Spares' },
        { id: 3, name: 'On Hold - Spares Supplied' },
        { id: 4, name: 'Completed' },
        { id: 5, name: 'Closed' }
    ];

    // Enhanced Analytics Calculations
    const analytics = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const thisMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        const nextMonth = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        const next90Days = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);

        // Filter equipment based on analytics filters
        const filteredEquipment = result.data.filter(equipment => {
            const installationDate = new Date(equipment.installation_date);
            const matchesDateRange = !analyticsDateRange[0] || !analyticsDateRange[1] || 
                (installationDate >= analyticsDateRange[0] && installationDate <= analyticsDateRange[1]);
            const matchesType = selectedAnalyticsType === 'all' || equipment.item.toLowerCase().includes(selectedAnalyticsType.toLowerCase());
            return matchesDateRange && matchesType;
        });

        // Basic KPIs
        const totalEquipment = filteredEquipment.length;
        const totalValue = filteredEquipment.reduce((sum, eq) => sum + parseFloat(eq.cost || 0), 0);
        const totalMaterialCost = filteredEquipment.reduce((sum, eq) => sum + parseFloat(eq.material_used || 0), 0);
        const averageAge = filteredEquipment.length > 0 ? 
            filteredEquipment.reduce((sum, eq) => {
                const installDate = new Date(eq.installation_date);
                const ageInYears = (now - installDate) / (1000 * 60 * 60 * 24 * 365);
                return sum + ageInYears;
            }, 0) / filteredEquipment.length : 0;

        // Functional Status Analysis
        const functionalStatusDistribution = {};
        filteredEquipment.forEach(eq => {
            const status = eq.functional_status || 'Unknown';
            functionalStatusDistribution[status] = (functionalStatusDistribution[status] || 0) + 1;
        });

        const functionalEquipment = filteredEquipment.filter(eq => 
            eq.functional_status === 'Functional' || eq.functional_status === 'Working').length;
        const nonFunctionalEquipment = totalEquipment - functionalEquipment;
        const functionalRate = totalEquipment > 0 ? (functionalEquipment / totalEquipment * 100) : 0;

        // Service Due Analysis
        const serviceDueIn30Days = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return false;
            const nextServiceDate = new Date(eq.next_year_date);
            const daysUntilService = (nextServiceDate - now) / (1000 * 60 * 60 * 24);
            return daysUntilService <= 30 && daysUntilService >= 0;
        }).length;

        const serviceDueIn90Days = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return false;
            const nextServiceDate = new Date(eq.next_year_date);
            const daysUntilService = (nextServiceDate - now) / (1000 * 60 * 60 * 24);
            return daysUntilService <= 90 && daysUntilService >= 0;
        }).length;

        const overdueService = filteredEquipment.filter(eq => {
            if (!eq.next_year_date) return false;
            const nextServiceDate = new Date(eq.next_year_date);
            return nextServiceDate < now;
        }).length;

        // Equipment Lifecycle Analysis
        const equipmentNearingEOL = filteredEquipment.filter(eq => {
            if (!eq.installation_date || !eq.lifespanvalue) return false;
            const installDate = new Date(eq.installation_date);
            const lifespanYears = parseInt(eq.lifespanvalue, 10);
            const maturityDate = new Date(installDate.getFullYear() + lifespanYears, installDate.getMonth(), installDate.getDate());
            const monthsRemaining = (maturityDate - now) / (1000 * 60 * 60 * 24 * 30);
            return monthsRemaining <= 12 && monthsRemaining > 0;
        }).length;

        const equipmentExpired = filteredEquipment.filter(eq => {
            if (!eq.installation_date || !eq.lifespanvalue) return false;
            const installDate = new Date(eq.installation_date);
            const lifespanYears = parseInt(eq.lifespanvalue, 10);
            const maturityDate = new Date(installDate.getFullYear() + lifespanYears, installDate.getMonth(), installDate.getDate());
            return maturityDate < now;
        }).length;

        // Service Frequency Analysis
        const highMaintenanceEquipment = filteredEquipment.filter(eq => 
            parseInt(eq.total_requets || 0) > 5).length;

        const serviceFrequencyDistribution = {};
        filteredEquipment.forEach(eq => {
            const frequency = parseInt(eq.total_requets || 0);
            let category;
            if (frequency === 0) category = 'No Service';
            else if (frequency <= 2) category = 'Low (1-2)';
            else if (frequency <= 5) category = 'Medium (3-5)';
            else category = 'High (>5)';
            
            serviceFrequencyDistribution[category] = (serviceFrequencyDistribution[category] || 0) + 1;
        });

        // Cost Analysis
        const avgEquipmentValue = totalEquipment > 0 ? totalValue / totalEquipment : 0;
        const avgMaintenanceCost = totalEquipment > 0 ? totalMaterialCost / totalEquipment : 0;
        const maintenanceCostRatio = totalValue > 0 ? (totalMaterialCost / totalValue * 100) : 0;

        // Equipment by Age Categories
        const ageDistribution = {
            'New (0-2 years)': 0,
            'Young (3-5 years)': 0,
            'Mature (6-10 years)': 0,
            'Old (>10 years)': 0
        };

        filteredEquipment.forEach(eq => {
            const installDate = new Date(eq.installation_date);
            const ageInYears = (now - installDate) / (1000 * 60 * 60 * 24 * 365);
            
            if (ageInYears <= 2) ageDistribution['New (0-2 years)']++;
            else if (ageInYears <= 5) ageDistribution['Young (3-5 years)']++;
            else if (ageInYears <= 10) ageDistribution['Mature (6-10 years)']++;
            else ageDistribution['Old (>10 years)']++;
        });

        // Facility Performance (if national)
        const facilityPerformance = isNational ? 
            clients.map(client => {
                const clientEquipment = filteredEquipment.filter(eq => eq.client === client.name);
                const clientFunctional = clientEquipment.filter(eq => 
                    eq.functional_status === 'Functional' || eq.functional_status === 'Working').length;
                const clientValue = clientEquipment.reduce((sum, eq) => sum + parseFloat(eq.cost || 0), 0);
                const clientMaintenanceCost = clientEquipment.reduce((sum, eq) => sum + parseFloat(eq.material_used || 0), 0);
                
                return {
                    facility: client.name,
                    totalEquipment: clientEquipment.length,
                    functionalEquipment: clientFunctional,
                    functionalRate: clientEquipment.length > 0 ? (clientFunctional / clientEquipment.length * 100) : 0,
                    totalValue: clientValue,
                    maintenanceCost: clientMaintenanceCost,
                    maintenanceRatio: clientValue > 0 ? (clientMaintenanceCost / clientValue * 100) : 0
                };
            }).filter(f => f.totalEquipment > 0) : [];

        // Critical Equipment Needing Attention
        const criticalEquipment = filteredEquipment.filter(eq => {
            const isOverdueService = eq.next_year_date && new Date(eq.next_year_date) < now;
            const isNonFunctional = eq.functional_status !== 'Functional' && eq.functional_status !== 'Working';
            const isHighMaintenance = parseInt(eq.total_requets || 0) > 5;
            const isNearingEOL = eq.installation_date && eq.lifespanvalue && (() => {
                const installDate = new Date(eq.installation_date);
                const lifespanYears = parseInt(eq.lifespanvalue, 10);
                const maturityDate = new Date(installDate.getFullYear() + lifespanYears, installDate.getMonth(), installDate.getDate());
                const monthsRemaining = (maturityDate - now) / (1000 * 60 * 60 * 24 * 30);
                return monthsRemaining <= 6;
            })();
            
            return isOverdueService || isNonFunctional || isHighMaintenance || isNearingEOL;
        });

        return {
            totalEquipment,
            totalValue,
            totalMaterialCost,
            averageAge,
            functionalStatusDistribution,
            functionalEquipment,
            nonFunctionalEquipment,
            functionalRate,
            serviceDueIn30Days,
            serviceDueIn90Days,
            overdueService,
            equipmentNearingEOL,
            equipmentExpired,
            highMaintenanceEquipment,
            serviceFrequencyDistribution,
            avgEquipmentValue,
            avgMaintenanceCost,
            maintenanceCostRatio,
            ageDistribution,
            facilityPerformance,
            criticalEquipment
        };
    }, [result.data, analyticsDateRange, selectedAnalyticsType, clients, isNational]);

    const handleEquipmentClick = (equipment) => {
        setSelectedEquipment(equipment);
        fetchTable2Data(equipment.asset_id);
    };
    
    const filteredDetails = selectedEquipment
        ? detailsResult.data.filter(v1 => v1.item === selectedEquipment.item)
        : detailsResult.data;

    useEffect(() => {
        fetchTable2Data();
    }, []);

    const fetchTable1Data = () => {
        const getAssignedToUsers = true;
        
        // For national users showing analytics, ensure we get ALL data
        if (isNational === 1 && showAnalytics) {
            // Remove facility filters for analytics to get all national data
            const originalClientId = sdataRef.current.client_id;
            sdataRef.current.client_id = null; // Remove client filter for analytics
            
            util.showLoader();
            TicketInventoryService.lists2(sdataRef.current, getAssignedToUsers).then(({ data }) => {
                setResult({ ...data.result });
                if (data.assigned_users) {
                    setAssignedUsers(data.assigned_users);
                }
                // Restore original client_id for filtered views
                sdataRef.current.client_id = originalClientId;
            }).catch(e => {
                message.error(e.message);
                sdataRef.current.client_id = originalClientId;
            }).finally(() => {
                util.hideLoader();
            });
        } else {
            util.showLoader();
            TicketInventoryService.lists2(sdataRef.current, getAssignedToUsers).then(({ data }) => {
                setResult({ ...data.result });
                if (data.assigned_users) {
                    setAssignedUsers(data.assigned_users);
                }
            }).catch(e => {
                message.error(e.message);
            }).finally(() => {
                util.hideLoader();
            });
        }
    };

    const fetchTable2Data = (equipmentId) => {
        const p = 1;
        const ps = 10;
        const getAssignedToUsers = true;

        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.ids = equipmentId || '';
        util.showLoader();
        TicketInventoryService.lists5(sdataRef.current, getAssignedToUsers).then(({ data }) => {
            setDetailsResult({ ...data.result });
            if (data.assigned_users) {
                setAssignedUsers(data.assigned_users);
            }
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        });
    };

    useEffect(() => {
        fetchTable1Data(0, 0, 'getAssignedToUsers');

        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            let allClients = res.data.result.data.map(v => { return { id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me } });
            setClients(allClients);
        });

        UserService.allUsers({ status: 1 }).then(res => {
            let allUsers = res.data.result.data.map(v => { return { id: v.id, name: v.name, role: v.role } });
            setUsers(allUsers);
        });

        AssetService.infrastuctureSubCats({ status: 1 }).then(res => {
            setInfrastuctureSubCats(res.data.result);
        });

        AssetService.all({ cat_type: 'Med/NonMed', my_only: 'Y' }).then(res => {
            setAssets([...res.data.result.data.map((v) => { return { id: v.id, name: ((v.serial_no ? (v.serial_no + ' - ') : '') + v.item_full_name) + ' [' + v.cat + ']' } })]);
        });

        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints([...res.data.result.data]);
        });

        return () => { message.destroy() }
    }, []);

    const downloadPdf = () => {
        MaintenanceReportUtil.generateStockTransactionsReport(sdataRef, util.Orientation.LANSCAPE);  
    }

    const download = () => {
        sdataRef.current.p = 1;
        sdataRef.current.ps = 0;
        util.showLoader();
        TicketInventoryService.lists2(sdataRef.current).then(({ data }) => {
            let header = {
                client: 'FACILITY',
                item: 'EQUIPMENT NAME',
                serial_no: 'SERIAL NUMBER',
                cost: 'VALUE',
                functional_status: 'FUNCTIONAL _STATUS',
                total_requets: 'REPORTED/ SERVICE FREQUENCY',
                material_used: 'MATERIAL USED',
                installation_date: 'INSTALLATION DATE',
                latest_date: 'LAST SERVICE/ FIXED DATE',
                next_year_date: 'NEXT ROUTINE SERVICE DATE',
            };

            let blob = util.convertToCSVBlob(data.result.data, header);
            saveAs(blob, "maintenance-service-report.csv");
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }

    function calculateMaturityDate(installationDate, lifespanValue) {
        const date = new Date(installationDate);
        const lifespanYears = parseInt(lifespanValue, 10);
        date.setFullYear(date.getFullYear() + lifespanYears);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ');
    }
    
    function calculateRemainingYears(installationDate, lifespanValue) {
        const maturityDate = new Date(installationDate);
        const lifespanYears = parseInt(lifespanValue, 10);
        maturityDate.setFullYear(maturityDate.getFullYear() + lifespanYears);
        const currentDate = new Date();
        const remainingYears = maturityDate.getFullYear() - currentDate.getFullYear();
        return remainingYears;
    }
            
    function calculateRemainingMonths(installationDate, lifespanValue) {
        const maturityDate = new Date(installationDate);
        const lifespanYears = parseInt(lifespanValue, 10);
        maturityDate.setFullYear(maturityDate.getFullYear() + lifespanYears);
        const currentDate = new Date();
        const remainingMonths = (maturityDate.getFullYear() - currentDate.getFullYear()) * 12 + (maturityDate.getMonth() - currentDate.getMonth());
        return remainingMonths;
    }

    const renderAnalyticsDashboard = () => (
        <div className="analytics-dashboard" style={{ marginBottom: 24, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
            {/* Toggle Button */}
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button 
                    onClick={() => setShowAnalytics(!showAnalytics)}
                    icon={showAnalytics ? <FallOutlined /> : <BarChartOutlined />}
                >
                    {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
                </Button>
            </div>

            {showAnalytics && (
                <>
                    {/* Filter Controls */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                            <RangePicker 
                                onChange={setAnalyticsDateRange}
                                style={{ width: '100%' }}
                                placeholder={['Installation From', 'Installation To']}
                            />
                        </Col>
                        <Col span={12}>
                            <Select 
                                value={selectedAnalyticsType} 
                                onChange={setSelectedAnalyticsType}
                                style={{ width: '100%' }}
                                placeholder="Filter by Equipment Type"
                            >
                            </Select>
                        </Col>
                    </Row>

                    {/* Critical Alerts */}
                    {(analytics.overdueService > 0 || analytics.nonFunctionalEquipment > 0 || analytics.equipmentExpired > 0) && (
                        <Alert
                            message="Maintenance Alerts"
                            description={
                                <div>
                                    {analytics.overdueService > 0 && <Tag color="red">Overdue Service: {analytics.overdueService}</Tag>}
                                    {analytics.nonFunctionalEquipment > 0 && <Tag color="red">Non-Functional: {analytics.nonFunctionalEquipment}</Tag>}
                                    {analytics.equipmentExpired > 0 && <Tag color="orange">Expired Equipment: {analytics.equipmentExpired}</Tag>}
                                    {analytics.serviceDueIn30Days > 0 && <Tag color="yellow">Service Due (30 days): {analytics.serviceDueIn30Days}</Tag>}
                                </div>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />
                    )}

                    {/* Key Performance Indicators */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Total Equipment"
                                    value={analytics.totalEquipment}
                                    prefix={<ToolOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Total Value"
                                    value={analytics.totalValue}
                                    prefix="E"
                                    precision={0}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Functional Rate"
                                    value={analytics.functionalRate}
                                    suffix="%"
                                    precision={1}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: analytics.functionalRate > 90 ? '#3f8600' : analytics.functionalRate > 70 ? '#fa8c16' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Average Age"
                                    value={analytics.averageAge}
                                    suffix=" years"
                                    precision={1}
                                    prefix={<ClockCircleOutlined />}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Secondary KPIs */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Service Due (30 days)"
                                    value={analytics.serviceDueIn30Days}
                                    prefix={<CalendarOutlined />}
                                    valueStyle={{ color: analytics.serviceDueIn30Days > 0 ? '#fa8c16' : '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="High Maintenance"
                                    value={analytics.highMaintenanceEquipment}
                                    prefix={<BugOutlined />}
                                    valueStyle={{ color: '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Maintenance Cost"
                                    value={analytics.totalMaterialCost}
                                    prefix="E"
                                    precision={0}
                                    valueStyle={{ color: '#fa8c16' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Cost Ratio"
                                    value={analytics.maintenanceCostRatio}
                                    suffix="%"
                                    precision={1}
                                    valueStyle={{ color: analytics.maintenanceCostRatio > 20 ? '#cf1322' : '#3f8600' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Charts and Analysis */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                            <Card title="Functional Status Distribution" style={{ height: 300 }}>
                                {Object.entries(analytics.functionalStatusDistribution).map(([status, count]) => (
                                    <div key={status} style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{status}</span>
                                            <span>{count} ({((count / analytics.totalEquipment) * 100).toFixed(1)}%)</span>
                                        </div>
                                        <Progress 
                                            percent={(count / analytics.totalEquipment * 100)} 
                                            showInfo={false}
                                            strokeColor={
                                                status === 'Functional' || status === 'Working' ? '#52c41a' :
                                                status === 'Non-Functional' ? '#ff4d4f' : '#fa8c16'
                                            }
                                        />
                                    </div>
                                ))}
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card title="Equipment Age Distribution" style={{ height: 300 }}>
                                {Object.entries(analytics.ageDistribution).map(([ageGroup, count]) => (
                                    <div key={ageGroup} style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{ageGroup}</span>
                                            <span>{count}</span>
                                        </div>
                                        <Progress 
                                            percent={analytics.totalEquipment > 0 ? (count / analytics.totalEquipment * 100) : 0} 
                                            showInfo={false}
                                            strokeColor={
                                                ageGroup.includes('New') ? '#52c41a' :
                                                ageGroup.includes('Young') ? '#1890ff' :
                                                ageGroup.includes('Mature') ? '#fa8c16' : '#ff4d4f'
                                            }
                                        />
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    </Row>

                    {/* Service Frequency Analysis */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                            <Card title="Service Frequency Distribution" style={{ height: 300 }}>
                                {Object.entries(analytics.serviceFrequencyDistribution).map(([frequency, count]) => (
                                    <div key={frequency} style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{frequency}</span>
                                            <span>{count}</span>
                                        </div>
                                        <Progress 
                                            percent={analytics.totalEquipment > 0 ? (count / analytics.totalEquipment * 100) : 0} 
                                            showInfo={false}
                                            strokeColor={
                                                frequency === 'No Service' ? '#ff4d4f' :
                                                frequency === 'Low (1-2)' ? '#52c41a' :
                                                frequency === 'Medium (3-5)' ? '#fa8c16' : '#cf1322'
                                            }
                                        />
                                    </div>
                                ))}
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card title="Equipment Lifecycle Status" style={{ height: 300 }}>
                                <div style={{ marginBottom: 16 }}>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Statistic
                                                title="Nearing EOL"
                                                value={analytics.equipmentNearingEOL}
                                                prefix={<WarningOutlined />}
                                                valueStyle={{ color: '#fa8c16' }}
                                            />
                                        </Col>
                                        <Col span={12}>
                                            <Statistic
                                                title="Expired Equipment"
                                                value={analytics.equipmentExpired}
                                                prefix={<ExclamationCircleOutlined />}
                                                valueStyle={{ color: '#cf1322' }}
                                            />
                                        </Col>
                                    </Row>
                                    <Alert
                                        message="Lifecycle Management"
                                        description={`${analytics.equipmentNearingEOL} equipment items will reach end-of-life within 12 months. ${analytics.equipmentExpired} items have already exceeded their expected lifespan.`}
                                        type="info"
                                        showIcon
                                        style={{ marginTop: 16 }}
                                    />
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Critical Equipment Table */}
                    {analytics.criticalEquipment.length > 0 && (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col span={24}>
                                <Card title="Critical Equipment Requiring Attention">
                                    <Table
                                        dataSource={analytics.criticalEquipment.slice(0, 10)}
                                        columns={[
                                            { title: 'Equipment', dataIndex: 'item', key: 'item', width: 200 },
                                            { title: 'Serial No', dataIndex: 'serial_no', key: 'serial_no', width: 120 },
                                            { 
                                                title: 'Status', 
                                                dataIndex: 'functional_status', 
                                                key: 'functional_status',
                                                width: 100,
                                                render: (status) => (
                                                    <Tag color={status === 'Functional' || status === 'Working' ? 'green' : 'red'}>
                                                        {status}
                                                    </Tag>
                                                )
                                            },
                                            { 
                                                title: 'Service Frequency', 
                                                dataIndex: 'total_requets', 
                                                key: 'total_requets',
                                                width: 120,
                                                render: (freq) => (
                                                    <Tag color={freq > 5 ? 'red' : freq > 2 ? 'orange' : 'green'}>
                                                        {freq || 0}
                                                    </Tag>
                                                )
                                            },
                                            { 
                                                title: 'Next Service', 
                                                dataIndex: 'next_year_date', 
                                                key: 'next_year_date',
                                                width: 150,
                                                render: (date) => {
                                                    if (!date) return <Tag color="gray">Not Scheduled</Tag>;
                                                    const nextService = new Date(date);
                                                    const now = new Date();
                                                    const isOverdue = nextService < now;
                                                    const daysUntil = Math.ceil((nextService - now) / (1000 * 60 * 60 * 24));
                                                    
                                                    return (
                                                        <div>
                                                            <div>{date}</div>
                                                            <Tag color={isOverdue ? 'red' : daysUntil <= 30 ? 'orange' : 'green'}>
                                                                {isOverdue ? `${Math.abs(daysUntil)} days overdue` : 
                                                                 daysUntil <= 0 ? 'Due today' :
                                                                 `${daysUntil} days left`}
                                                            </Tag>
                                                        </div>
                                                    );
                                                }
                                            },
                                            { 
                                                title: 'Value', 
                                                dataIndex: 'cost', 
                                                key: 'cost',
                                                width: 100,
                                                render: (cost) => `E${parseFloat(cost || 0).toLocaleString()}`
                                            }
                                        ]}
                                        pagination={{ pageSize: 5, showSizeChanger: false }}
                                        size="small"
                                        rowKey="serial_no"
                                        scroll={{ x: 'max-content' }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Facility Performance (if national) */}
                    {isNational && analytics.facilityPerformance.length > 0 && (
                        <Row gutter={16}>
                            <Col span={24}>
                                <Card title="Facility Performance Comparison">
                                    <Table
                                        dataSource={analytics.facilityPerformance}
                                        columns={[
                                            { title: 'Facility', dataIndex: 'facility', key: 'facility' },
                                            { title: 'Total Equipment', dataIndex: 'totalEquipment', key: 'totalEquipment' },
                                            { title: 'Functional', dataIndex: 'functionalEquipment', key: 'functionalEquipment' },
                                            { 
                                                title: 'Functional Rate', 
                                                dataIndex: 'functionalRate', 
                                                key: 'functionalRate',
                                                render: (rate) => (
                                                    <Tag color={rate > 90 ? 'green' : rate > 70 ? 'orange' : 'red'}>
                                                        {rate.toFixed(1)}%
                                                    </Tag>
                                                )
                                            },
                                            { title: 'Total Value', dataIndex: 'totalValue', key: 'totalValue',
                                              render: (value) => `E${value.toFixed(0)}`
                                            },
                                            { title: 'Maintenance Cost', dataIndex: 'maintenanceCost', key: 'maintenanceCost',
                                              render: (cost) => `E${cost.toFixed(0)}`
                                            },
                                            { 
                                                title: 'Maintenance Ratio', 
                                                dataIndex: 'maintenanceRatio', 
                                                key: 'maintenanceRatio',
                                                render: (ratio) => (
                                                    <Tag color={ratio > 20 ? 'red' : ratio > 10 ? 'orange' : 'green'}>
                                                        {ratio.toFixed(1)}%
                                                    </Tag>
                                                )
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                        rowKey="facility"
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Maintenance & Service History Summary</h2>
                </div>
                <div className="my-auto ml-auto"></div>
            </div>

            {/* Analytics Dashboard */}
            {renderAnalyticsDashboard()}

            <div className="page-pad">
                {result.data.length > 0 &&
                    <div className="text-secondary mb8">
                        Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
                    </div>
                }

                <div className="d-flex tbl-search-head">
                    <div className="my-auto flex-grow-1 pr10">
                        <SearchForm
                            dataRef={sdataRef}
                            onSearch={fetchTable1Data}
                            clients={clients}
                            ticketStatusNames={ticketStatusNames}
                            assignedUsers={assignedUsers}
                            isNational={isNational}
                        />
                    </div>

                    <div className="my-auto d-flex" style={{ display: 'flex', alignItems: 'center' }}>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={download} 
                            style={{
                                backgroundColor: 'green', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={excel} alt="download" height={15} /> Export Excel
                        </button>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={downloadPdf} 
                            style={{
                                backgroundColor: 'red', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={acrobat} alt="download" height={15} /> Export PDF
                        </button>
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm font-xs table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    <th className="w20">SN</th>
                                    {isNational === 1 && <th className="w200">Facility</th>}
                                    <th className="w500">EQUIPMENT NAME</th>
                                    <th className="w500">SERIAL NUMBER</th>
                                    <th>VALUE: SZL</th>
                                    <th>FUNCTIONAL STATUS</th>
                                    <th className="w200">REPORTED / SERVICE FREQUENCY</th>
                                    <th className="w200">MATERIAL USED</th>
                                    <th className="w200">INSTALLATION DATE</th>
                                    <th className="w200">LAST REPORTED DATE</th>
                                    <th className="w200">ROUTINE NEXT SERVICE</th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {result.data.map((v, i) => {
                                    const nextServiceDate = new Date(v.next_year_date);
                                    const currentDate = new Date();
                                    const timeDifference = nextServiceDate - currentDate;
                                    const daysDifference = timeDifference / (1000 * 3600 * 24);
                                    const isLessThan30Days = daysDifference < 30;

                                    return (
                                        <tr key={i} onClick={() => handleEquipmentClick(v)}>
                                            <td>{i + 1}.</td>
                                            {isNational === 1 && <td>{v.client}</td>}

                                            <td><div className="uc bold600">{v.item}</div>
                                            <div className="d-flex"><div className="w90">Service</div> : <div className="bold600 pl5">Every {v.service_period} {v.service_period_type}</div></div>
                                <div className="d-flex"><div className="w90">Life Span</div> : <div className="bold600 pl5">{v.lifespanvalue} {v.lifespanunit}</div></div>
                                <div className="d-flex"><div className="w90">Maturity Date</div> : <div className="bold600 pl5">{calculateMaturityDate(v.installation_date,v.lifespanvalue)}</div></div>
                                <div className="d-flex"><div className="w90">Remaining Time</div> : <div className="bold600 pl5">{calculateRemainingYears(v.installation_date,v.lifespanvalue)} Years or {calculateRemainingMonths(v.installation_date,v.lifespanvalue)} Months</div></div>
                                            </td>

                                            <td>{v.serial_no}</td>
                                            <td>E{v.cost}</td>
                                            <td>{v.functional_status}</td>
                                            <td>{v.total_requets}</td>
                                            <td>E{v.material_used}</td>
                                            <td>{v.installation_date}</td>
                                            <td>{v.latest_date}</td>
                                            <td style={{ color: isLessThan30Days ? 'red' : 'inherit' }}>{v.next_year_date}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex tbl-foot-bx">
                        <AntdPaging
                            onChange={fetchTable1Data}
                            total={result.page.total_records}
                            current={result.page.cur_page}
                            pageSize={sdataRef.current.ps}
                            showSizeChanger
                        />
                    </div>
                </If>

                {selectedEquipment && (
                    <div className="table-responsive">
                        <h3>Selected Equipment Details</h3>
                        <table className="table table-bordered table-sm font-xs table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    {isNational === 1 && <th className="w200">Facility</th>}
                                    <th className="w300">EQUIPMENT NAME</th>
                                    <th className="w100">SERIAL NUMBER</th>
                                    <th className="w300">PROBLEM REPORTED</th>
                                    <th className="w300">DESCRIPTION</th>
                                    <th className="w1500">DATE REPORTED</th>
                                    <th className="w200">ASSIGNED TO</th>
                                    <th className="w100">JOB STATUS</th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {filteredDetails.map((v1, i1) => (
                                    <tr key={i1}>
                                        {isNational === 1 && <td>{v1.client}</td>}
                                        <td>{v1.item}</td>
                                        <td>{v1.asset_id}</td>
                                        <td>{v1.problem}</td>
                                        <td>{v1.detailed_problem}</td>
                                        <td>{v1.created}</td>
                                        <td>{v1.assigned_to_name}</td>
                                        <td>{v1.status_name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>
        </div>
    );
};

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, ticketStatusNames, isNational } = props;
    let [data, setData] = useState({ ...dataRef.current });
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [items, setItems] = useState([]);

    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') {
            data.assigned_to = null;
        }
        setData({ ...data });
    }
    useEffect(() => {
        dataRef.current = { ...data };
    }, [data]);

    useEffect(() => {
        let ob = clients.find(v => v.is_me);
        setData({ ...data, client_id: ob?.id });
    }, [clients]);

    useEffect(() => {
        setAssignedUsers(props.assignedUsers);
    }, [props.assignedUsers]);

    useEffect(() => {
        AssetService.allItems({status:1}).then(res=>{
            let allItems=[];
            allItems=res.data.result.data.map((v)=>{
                let item_full_name=v.item_full_name+' ['+(v.cat_type==='Infrastructure'?v.subcat:v.cat)+']';
                return {label:item_full_name, value:v.id};
            });
            setItems(allItems);
        });

        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                {isNational === 1 &&
                    <div className="w150 mr5">
                        <AntdSelect
                            placeholder="Facility (All)"
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => handleChange(v, 'client_id')}
                        />
                    </div>
                }

                <div className="w400 mr5">
                    <AntdSelect 
                        placeholder="Item (All)" 
                        allowClear 
                        showSearch 
                        options={items.map(v => { return { id: v.id, name: v.item_full_name } })}
                        value={data.item_id}
                        disabled={data.id}
                        onChange={v => handleChange(v, 'item_id')} 
                    />
                </div>

                <div className="w300 mr5">
                    <AntdDatepicker
                        placeholder="From Date"
                        allowClear
                        value={data.from_date}
                        onChange={v => {
                            if (data.to_date && moment(v) > moment(data.to_date)) {
                                message.error("From date should be less than or equal to To Date");
                                v = null;
                            }
                            handleChange(v, 'from_date');
                        }}
                    />
                </div>

                <div className="w300 mr5">
                    <AntdDatepicker
                        placeholder="To Date"
                        allowClear
                        value={data.to_date}
                        onChange={v => {
                            if (data.from_date && moment(v) < moment(data.from_date)) {
                                message.error("To date should be greater than or equal to From Date");
                                v = null;
                            }
                            handleChange(v, 'to_date');
                        }}
                    />
                </div>

                <Button className="mt3" type="primary" onClick={() => onSearch()}>
                    <img src={search} alt='' height={15}/>
                </Button>
            </div>
        </form>
    )
}

export default Maintenance